import {
  schlageAccessState,
  isInstalledSchlageAccess,
} from "../../../../common/schlageAccessState";
import React from "react";
import {
  Box,
  Button,
  Heading,
  Layer,
  Spinner,
  TableCell,
  Text,
  Tip,
} from "grommet";
import {
  AddCircle,
  CheckboxSelected,
  Refresh,
  StatusGood,
  StatusWarning,
  Subtract,
  SubtractCircle,
} from "grommet-icons";
import { useTranslation } from "react-i18next";

export default function KeysSentReportModal({
  onClose,
  report,
  isOracode,
  isSchlage,
  canUseOfflineFallback = false,
  onRetryWithOfflineCodes,
  onRefresh,
}) {
  const { t } = useTranslation();
  const {
    ekeysDoors = [],
    passcodesDoors = [],
    notifStatus = {},
  } = report || {};
  const accessState = (result) =>
    schlageAccessState(
      result?.code && {
        ...result.code,
        door: result.code.door || result.door,
      },
      { lockType: isSchlage ? "schlage" : isOracode ? "oracode" : "ttlock" },
    );
  const pendingSpinner = (label) => (
    <Spinner size="small" role="status" aria-label={label} />
  );

  const renderIcon = (result) => {
    const dropProps = { align: { left: "right" } };
    if (result?.confirmationError) {
      return (
        <Tip content={t(result.confirmationError)} dropProps={dropProps}>
          <StatusWarning color="status-error" />
        </Tip>
      );
    }
    if (typeof result === "boolean") {
      return result ? (
        <CheckboxSelected color="status-ok" size="20px" />
      ) : (
        <Subtract color="text" size="20px" />
      );
    }
    const state = accessState(result);
    if (report.timedOut && ["pending", "deleting"].includes(state)) {
      return (
        <Tip content={t("keysReport.confirmationTimeout")} dropProps={dropProps}>
          <StatusWarning color="status-warning" />
        </Tip>
      );
    }
    if (["pending", "deleting"].includes(state)) return null;
    const action = result?.status === "pending"
      ? result.code?.revocationRequested
        ? "delete"
        : result.code?.pendingOperation || "add"
      : result?.status;
    const failedState = [
      "failed", "deleteFailed", "changeFailed", "unconfirmed", "removalUnconfirmed",
    ].includes(state);
    if (failedState) {
      return (
        <Tip content={t(`accessList.schlage.${state}`)} dropProps={dropProps}>
          <StatusWarning color="status-error" />
        </Tip>
      );
    }
    switch (action) {
      case "keep":
        return (
          <Tip content={t("keysReport.noChange")} dropProps={dropProps}>
            <StatusGood color="blue-1" />
          </Tip>
        );
      case "delete":
        return (
          <Tip content={t("keysReport.removed")} dropProps={dropProps}>
            <SubtractCircle color="status-warning" />
          </Tip>
        );
      case "add":
        return (
          <Tip content={t("keysReport.added")} dropProps={dropProps}>
            <AddCircle color="status-ok" />
          </Tip>
        );
      case "update":
        return (
          <Tip content={t("keysReport.updated")} dropProps={dropProps}>
            <Refresh color="status-ok" />
          </Tip>
        );
      case "error":
        return (
          <Tip content={result.message} dropProps={dropProps}>
            <StatusWarning color="status-error" />
          </Tip>
        );
      default:
        return null;
    }
  };

  const renderPin = (result) => {
    if (result.confirmationError) return <Text>—</Text>;
    const state = accessState(result);
    if (["pending", "deleting"].includes(state) && !report.timedOut) {
      return pendingSpinner(t(`accessList.schlage.${state}`));
    }
    if (result.status === "delete" || (state && !isInstalledSchlageAccess(state))) {
      return <Text>—</Text>;
    }
    return <Text>{result.code?.keyboardPwd}</Text>;
  };

  const renderChannel = (channel) => {
    const pending = report.deliveryPending &&
      report.pendingChannels?.[channel] && notifStatus[channel] !== true;
    if (pending && !report.timedOut) {
      return pendingSpinner(t("keysReport.channelPending", {
        channel: t(`keysReport.${channel}`),
      }));
    }
    return renderIcon(notifStatus[channel] ?? false);
  };

  return (
    report && (
      <Layer position="center" onClickOutside={onClose} onEsc={onClose}>
        <Box
          pad="medium"
          gap="small"
          width="large"
          height="stretch"
          alignSelf="start"
        >
          <Heading level={3} margin="none">
            {t("keysReport.title")}
          </Heading>
          {report.timedOut && (
            <Text role="status" color="status-warning">
              {t("keysReport.confirmationTimeout")}
            </Text>
          )}
          {report.confirmationError && (
            <Text role="alert" color="status-error">
              {t("keysReport.confirmationRefreshError")}
            </Text>
          )}
          {report.error && !passcodesDoors.length ? (
            <Text color="status-error">
              {t("keysReport.error", { error: report.error })}
            </Text>
          ) : report.loading ? (
            <Box height="medium" align="center" justify="center">
              <Spinner size="large" margin="small" />
              <Text>{t("keysReport.loading")}</Text>
            </Box>
          ) : (
            <>
              {report.error && <Text color="status-error">{report.error}</Text>}
              <Box
                background="light-3"
                round="small"
                pad="medium"
                margin={{ bottom: "small" }}
              >
                <Heading level={4} margin={{ bottom: "small", top: "none" }}>
                  {t("keysReport.communications")}
                </Heading>
                {report.deliveryError && (
                  <Text color="status-error" role="alert">
                    {t("keysReport.deliveryFailed")}
                  </Text>
                )}
                <table style={{ width: "fit-content" }}>
                  <tbody>
                    <tr>
                      <TableCell>
                        <Text>{t("keysReport.text")} </Text>
                      </TableCell>
                      <TableCell>{renderChannel("text")}</TableCell>
                    </tr>
                    <tr>
                      <TableCell>
                        <Text>{t("keysReport.email")} </Text>
                      </TableCell>
                      <TableCell>{renderChannel("email")}</TableCell>
                    </tr>
                    <tr>
                      <TableCell>
                        <Text>{t("keysReport.push")} </Text>
                      </TableCell>
                      <TableCell>{renderChannel("push")}</TableCell>
                    </tr>
                  </tbody>
                </table>
              </Box>

              <Box
                background="light-3"
                round="small"
                pad="medium"
                margin={{ bottom: "small" }}
              >
                <Heading level={4} margin={{ bottom: "small", top: "none" }}>
                  {t("passcodes.title")}
                </Heading>
                <table style={{ width: "fit-content" }}>
                  <tbody>
                    {[...passcodesDoors]
                      .sort((a) =>
                        a.door?.type === "area" || a.code?.door?.type === "area"
                          ? 1
                          : -1,
                      )
                      .map((result) => (
                        <tr key={result.code?._id ||
                          result.code?.door?._id || result.door?._id}>
                          <TableCell>
                            {result.door?.type === "area" ||
                            result.code?.door?.type === "area"
                              ? result.code?.door?.name || result.door?.name
                              : t("accessList.roomName", {
                                  room:
                                    result.code?.door?.name || result.door?.name,
                                })}
                          </TableCell>
                          <TableCell>
                            <Box
                              border={{ size: "small", color: "text" }}
                              round
                              pad={{ horizontal: "6px" }}
                              align="center"
                              justify="center"
                            >
                              {renderPin(result)}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {renderIcon(result)}
                            {result.status === "error" && result.message && (
                              <Text color="status-error" size="small">
                                {result.message}
                              </Text>
                            )}
                            {result.confirmationError && (
                              <Text color="status-error" size="small">
                                {t(result.confirmationError)}
                              </Text>
                            )}
                          </TableCell>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </Box>
              {(ekeysDoors.length > 0 || (!isOracode && !isSchlage)) && (
                <Box
                  background="light-3"
                  round="small"
                  pad="medium"
                  margin={{ bottom: "small" }}
                >
                  <Heading level={4} margin={{ bottom: "small", top: "none" }}>
                    {t("guestDetails.mobileKeys")}
                  </Heading>
                  <table style={{ width: "fit-content" }}>
                    <tbody>
                      {[...ekeysDoors]
                        .sort((a) => (a.door?.type === "area" ? 1 : -1))
                        .map((result) => (
                          <tr key={result.door?.name}>
                            <TableCell>
                              <Text>
                                {result.door?.type === "area"
                                  ? result.door?.name
                                  : t("accessList.roomName", {
                                      room: result.door?.name,
                                    })}
                              </Text>
                            </TableCell>
                            <TableCell>
                              <Box direction="row" gap="small" align="center">
                                {renderIcon(result)}
                                {result.status === "error" &&
                                  result.message && (
                                    <Text color="status-error" size="small">
                                      {result.message}
                                    </Text>
                                  )}
                              </Box>
                            </TableCell>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </Box>
              )}
            </>
          )}
          <Box direction="row" gap="small" alignSelf="center">
            {onRefresh && (report.timedOut || report.confirmationError) && (
              <Button
                label={t("keysReport.refreshStatus")}
                onClick={onRefresh}
                disabled={report.loading}
                small
              />
            )}
            {passcodesDoors?.some((r) => r.status === "error" &&
              ((r.code?.issuer?.lockType || r.code?.lockType || r.code?.door?.lockType || r.door?.lockType)
                ? (r.code?.issuer?.lockType || r.code?.lockType || r.code?.door?.lockType || r.door?.lockType) === "ttlock"
                : canUseOfflineFallback)) && (
                <Button
                  label={t("keysReport.retryOffline")}
                  onClick={onRetryWithOfflineCodes}
                  primary
                  color="status-warning"
                  small
                  alignSelf="center"
                />
              )}
            <Button
              label={t("common.close")}
              onClick={onClose}
              color="dark-3"
              disabled={report.loading}
              small
              alignSelf="center"
            />
          </Box>
        </Box>
      </Layer>
    )
  );
}
